import React, { useState, useEffect, useMemo} from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  LabelList
} from "recharts";
import { FaSearch, FaExclamationTriangle,FaShieldAlt, FaPen, FaCalendarAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import UserSidebar from "./UserSidebar";
import UserHeader from "./UserHeader";
import { apiFetch } from "../api_call"; 
import "./Dashboard.css";

const COLORS = ["#7c3aed", "#f50bed", "#d02cec"];

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [globalSearch, setGlobalSearch] = useState("");
  const [totalDepartments, setTotalDepartments] = useState(0);
  const [totalCompliances, setTotalCompliances] = useState(0);
  const [endSubscription, setEndSubscription] = useState("");
  const [complianceScore, setComplianceScore] = useState(0);
  const [showDefenseModal, setShowDefenseModal] = useState(false);
  const [totalInstances, setTotalInstances] = useState(0);
  const [totalInstancesOverdue, setTotalInstancesOverdue] = useState(0);
const [approvedOverdueInstances, setApprovedOverdueInstances] = useState(0);



  // ======== MOVED pieTrafficData INSIDE STATE (dynamic from API) ========
  const [pieTrafficData, setPieTrafficData] = useState([
    { name: "Low Risk", value: 0 },
    { name: "Compliant", value: 0 },
    { name: "At Risk", value: 0 },
  ]);

  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null); 
  const [showModal, setShowModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
const [daysLeft, setDaysLeft] = useState(0);
const [departments, setDepartments] = useState([]);
const [userDepartment, setUserDepartment] = useState("");


  // Compliance modal state
const [showComplianceModal, setShowComplianceModal] = useState(false);

const [complianceBreakdown, setComplianceBreakdown] = useState({
  data_compliance: 0,
  statutory_compliance: 0,
  data_breach: 0,   
  custom_compliance: 0,
});

  const navigate = useNavigate();

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    if (savedUser) setUser(JSON.parse(savedUser));
  }, []);

  // Dashboard summary
 useEffect(() => {
  const fetchSummary = async () => {
    try {
      setSummaryLoading(true);

      const data = await apiFetch("/dashboard/summary", { 
        method: "GET", 
        credentials: "include" 
      });

      if (!data || data.error) return;

      setTotalDepartments(data.total_departments || 0);
      setDepartments(data.department_names || []);
      setUserDepartment(data.user_department || "");
      setTotalCompliances(data.total_compliances || 0);
      setComplianceScore(data.compliance_score_percent || 0);
      setEndSubscription(data.subscription_end_date || "");
      setTotalInstances(data.total_instances || 0);

setTotalInstancesOverdue(Number(data.total_instances_overdue) || 0);
setApprovedOverdueInstances(Number(data.approved_overdue_instances) || 0);

setComplianceScore(Number(data.compliance_score_percent) || 0);

      setComplianceBreakdown({
  data_compliance: data.data_compliance || 0,
  statutory_compliance: data.statutory_compliance || 0,
  data_breach: data.data_breach || 0,   
  custom_compliance: data.custom_compliance || 0,
});

      const atRisk = Number(data.at_risk_percent) || 0;
const compliant = Number(data.no_risk_percent) || 0;
const lowRisk = Number(data.low_risk_percent) || 0;

setPieTrafficData([
  { name: "At Risk", value: atRisk },
  { name: "Compliant", value: compliant },
  { name: "Low Risk", value: lowRisk },
]);

    } catch (err) {
      console.log("Dashboard API Error:", err);
    } finally {
      setSummaryLoading(false);
    }
  };

  fetchSummary();
}, []);
// Inside Dashboard component, after state declarations


  // Parse backend date
  const parseBackendDate = (input) => {
    if (!input) return null;
    if (input instanceof Date) {
      const y = input.getFullYear();
      const m = String(input.getMonth() + 1).padStart(2, "0");
      const d = String(input.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    if (typeof input === "string") {
      if (input.includes("-")) {
        const parts = input.split("-");
        if (parts.length === 3) {
          const [day, month, year] = parts;
          return `${year}-${month}-${day}`;
        }
      }
      return input;
    }
    return null;
  };

  const handleComplianceClick = () => {
  setShowComplianceModal(true);
};

const handleLegendClick = (data) => {
  const name = data.value;

  if (name === "At Risk") {
    navigate("/at-risk_");
  } else if (name === "Low Risk") {
    navigate("/low-risk_");
  } else if (name === "Compliant") {
    navigate("/compliant_");
  }
};

const handleViewCompliance = (type) => {

  if (type === "data") {
    localStorage.setItem("compliance_type", "data-compliance");
    navigate("/manage-statutory");
  }

  else if (type === "statutory") {
    localStorage.setItem("compliance_type", "statutory");
    navigate("/manage-statutory");
  }
  else if (type === "breach") {
  navigate("/manage-data-breach");
}

  else if (type === "custom") {
    navigate("/manage-self");
  }

  setShowComplianceModal(false);
};
const defenseData = React.useMemo(() => [
  { name: "Compliant", value: Number(approvedOverdueInstances) || 0 },
  { 
    name: "Non-Compliant", 
    value: Math.max(Number(totalInstancesOverdue) - Number(approvedOverdueInstances), 0)
  }
], [approvedOverdueInstances, totalInstancesOverdue]);
const handleSubscriptionClick = () => {
  if (!endSubscription) return;

  // parse DD-MM-YYYY to Date
  const parts = endSubscription.split("-");
  let endDate;

  if (parts.length === 3) {
    const [day, month, year] = parts;
    endDate = new Date(year, month - 1, day);
  } else {
    endDate = new Date(endSubscription);
  }

  const today = new Date();

  // remove time part for accurate day calculation
  today.setHours(0,0,0,0);
  endDate.setHours(0,0,0,0);

  const diffTime = endDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  setDaysLeft(diffDays);
  setShowSubscriptionModal(true);
};



  

  // Fetch calendar events
  const fetchEvents = async () => {
    try {
      const data = await apiFetch("/calender/user/calendar");
      if (data && Array.isArray(data.events)) {
        const mappedEvents = data.events.map((item) => ({
          id: item.id,
          title: item.title,
          start: parseBackendDate(item.date),
          allDay: true,
          extendedProps: {
            status: item.status,
            type: item.type,
          },
        }));
        setEvents(mappedEvents);
      }
    } catch (err) {
      console.error("Calendar API Error:", err);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  // Handle day click to show modal
  const handleDateClick = (info) => {
    setSelectedDate(parseBackendDate(info.date));
    setShowModal(true);
  };

  // Events for selected date
  const eventsForSelectedDate = events.filter(e => e.start === selectedDate);
  const thStyle = {
  borderBottom: "1px solid #ddd",
  padding: "10px",
  textAlign: "left",
};

const tdStyle = {
  padding: "10px",
  borderBottom: "1px solid #eee",
};

const handleDefenseClick = (data) => {
  if (data.name === "Compliant") {
    navigate("/compliant-_");
  } else {
    navigate("/non-compliant_"); // or pending page if you have one
  }
};


  return (
    <div className="Dashboard">
      <UserSidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <UserHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <div className="headSection">
        <div></div>
        <div className="rightGroup">
          <div className="finderBox">
  <input
    type="text"
    placeholder="Can't find something? Search it here!"
    value={globalSearch}
    onChange={(e) => setGlobalSearch(e.target.value)}
    onKeyDown={(e) => {
      if (e.key === "Enter" && globalSearch.trim() !== "") {
        localStorage.setItem("globalSearch", globalSearch);
        navigate("/comprehensive_");
      }
    }}
  />
  <FaSearch className="finderIcon" />
</div>
        </div>
      </div>

      <div className="welcome-bar">
        <div className="welcome-user">Hello,<br />{user ? user.usrlst_name : "Admin"}</div>
        <div
  className="welcome-score"
  style={{ cursor: "pointer" }}
  onClick={() => setShowDefenseModal(true)}
>
  Defensive Strength: {complianceScore}%
</div>
      </div>

      <section className="charts_">
        <div className="chart-row_ top">
       <div className="chart-card_">
  <h3 style={{ display: "flex", alignItems: "center", gap: "8px" }}>
  Compliance Health

  <span className="info-icon">
    i
    <span className="tooltip-text">
      <strong>At Risk Compliances:</strong> Compliances that have not been approved and are past their action dates<br />
      <strong>Low Risk Compliances:</strong> Compliances that are either pending for review or are completed past their action dates<br />
      <strong>Compliant:</strong> Compliances that have been approved before their action date
    </span>
  </span>
</h3>

  {summaryLoading ? (
    <div
      style={{
        height: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "500",
        color: "#777"
      }}
    >
      Loading...
    </div>
  ) : pieTrafficData.some(d => d.value > 0) ? (
    <ResponsiveContainer width="100%" height={200}>
      <PieChart>
        <Pie
  data={pieTrafficData}
  dataKey="value"
  nameKey="name"
  cx="40%"
  cy="50%"
  innerRadius={30}
  outerRadius={70}
  label={({ percent }) => `(${(percent * 100).toFixed(0)}%)`}
  cursor="pointer"
  onClick={(data) => handleLegendClick({ value: data.name })}
>
          {pieTrafficData.map((entry, index) => (
            <Cell key={index} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>

        <Tooltip
  formatter={(value, name) => {
    const total = pieTrafficData.reduce((sum, entry) => sum + entry.value, 0);
    const percent = total ? ((value / total) * 100).toFixed(0) : 0; // match label
    return [`${percent}%`, name];
  }}
/>

        <Legend
          layout="vertical"
          verticalAlign="middle"
          align="right"
          onClick={handleLegendClick}
          wrapperStyle={{ cursor: "pointer" }}
        />
      </PieChart>
    </ResponsiveContainer>
  ) : (
    <div
      style={{
        height: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#555",
        fontWeight: "500",
        fontSize: "16px",
      }}
    >
      No Compliance found
    </div>
  )}
</div>


          <div className="chart-card_ calendar-card">
            <h3 style={{ display: "flex", alignItems: "center", gap: "8px" }}>
  Compliance Calender

  <span className="info-icon">
    i
    <span className="tooltip-text">
      Date Wise Action Dates for each Instance
    </span>
  </span>
</h3>
            <div className="calendar-wrapper">
              <FullCalendar
  plugins={[dayGridPlugin, interactionPlugin]}
  initialView="dayGridMonth"
  height="90%"
  contentHeight="auto"
  aspectRatio={1.75}
  expandRows={true}
  events={events}
  dateClick={handleDateClick}
  headerToolbar={{
    left: "title",
    center: "",
    right: "prev,next",
  }}
  dayCellContent={(arg) => {
    const dateStr = parseBackendDate(arg.date);
    const dayTasks = events.filter(e => e.start === dateStr);

    // More than 1 task → show blue dot
    if (dayTasks.length >=1) {
      return {
        html: `
          <div style="text-align:center;font-weight:500;">
            ${arg.dayNumberText}
          </div>
          <div
            style="
              margin:2px auto 0;
              width:6px;
              height:6px;
              background:#3b82f6; /* blue dot */
              border-radius:50%;
            "
          ></div>
        `,
      };
    }

    // Exactly 1 task → show its text
    if (dayTasks.length === 1) {
      const t = dayTasks[0];
      return {
        html: `
          <div style="text-align:center;font-weight:500;">
            ${arg.dayNumberText}
          </div>
          <div
            title="${t.title}"
            style="
              font-size:10px;
              margin-top:2px;
              white-space:nowrap;
              overflow:hidden;
              text-overflow:ellipsis;
              text-align:center;
            "
          >
            ${t.title.length > 15 ? t.title.slice(0,15) + "..." : t.title}
          </div>
        `,
      };
    }
    

    // No task → show just day number
    return {
      html: `<div style="text-align:center;font-weight:500;">${arg.dayNumberText}</div>`,
    };
  }}
  dayCellClassNames={() => "fc-pointer"}
/>

            </div>
          </div>
        </div>

       <section
  className="stats"
  style={{
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: "16px",
  }}
>
  <div
  className="stat-box"
  onClick={() => navigate("/manage-data-breach")}
  style={{
    minWidth: 0,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    cursor: "pointer"
  }}
>
  <div className="icon">
    <FaExclamationTriangle  /> 
  </div>

  <div className="content" style={{ minWidth: 0, overflow: "hidden" }}>
    <div
      className="value"
      style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
    >
      {complianceBreakdown.data_breach}
    </div>

    <div
  className="label"
  style={{
    whiteSpace: "normal",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    lineHeight: "1.2"
  }}
>
  Data Breach Management
</div>
  </div>
</div>

 <div
  className="stat-box"
  onClick={handleComplianceClick}
  style={{
    minWidth: 0,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    cursor: "pointer"
  }}
>

    <div className="icon"><FaShieldAlt /></div>
    <div className="content" style={{ minWidth: 0, overflow: "hidden" }}>
      <div
        className="value"
        style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {totalCompliances}
      </div>
      <div
        className="label"
        style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
      >
        Compliance
      </div>
    </div>
  </div>

<div
  className="stat-box"
  onClick={() => navigate("/comprehensive_")}
  style={{
    minWidth: 0,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    cursor: "pointer"
  }}
>

    <div className="icon"><FaPen /></div>
    <div className="content" style={{ minWidth: 0, overflow: "hidden" }}>
      <div
        className="value"
        style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {totalInstances}
      </div>
      <div
        className="label2"
        style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
      >
        Instances
      </div>
    </div>
  </div>

 <div
  className="stat-box"
  onClick={handleSubscriptionClick}
  style={{
    minWidth: 0,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    cursor: "pointer"
  }}
>

    <div className="icon"><FaCalendarAlt /></div>
    <div className="content" style={{ minWidth: 0, overflow: "hidden" }}>
      <div
        className="value1"
        style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {endSubscription}
      </div>
      <div
  className="label1"
  style={{
    whiteSpace: "normal",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    lineHeight: "1.2"
  }}
>
  End of Subscription
</div>
    </div>
  </div>
</section>

      </section>
      {/* Compliance Breakdown Modal */}
{showComplianceModal && (
  <div
    className="ct-modal-backdrop"
    onClick={() => setShowComplianceModal(false)}
  >
    <div
      className="ct-modal"
      onClick={(e) => e.stopPropagation()}
      style={{ width: "500px" }}
    >
      <h3>Compliance Breakdown</h3>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={thStyle}>Type</th>
            <th style={thStyle}>View</th>
            <th style={thStyle}>Count</th>
          </tr>
        </thead>

        <tbody>

         

           <tr>
            <td style={tdStyle}>Data Compliance</td>
            <td style={tdStyle}>
              <button
                className="view-btn"
                onClick={() => handleViewCompliance("data")}
              >
                View
              </button>
            </td>
            <td style={tdStyle}>{complianceBreakdown.data_compliance}</td>
          </tr>
          

          <tr>
            <td style={tdStyle}>Custom Compliance</td>
            <td style={tdStyle}>
              <button
                className="view-btn"
                onClick={() => handleViewCompliance("custom")}
              >
                View
              </button>
            </td>
            <td style={tdStyle}>{complianceBreakdown.custom_compliance}</td>
          </tr>
          <tr>
            <td style={tdStyle}>Statutory Compliance</td>
            <td style={tdStyle}>
              <button
                className="view-btn"
                onClick={() => handleViewCompliance("statutory")}
              >
                View
              </button>
            </td>
            <td style={tdStyle}>{complianceBreakdown.statutory_compliance}</td>
          </tr>
          
          

        </tbody>
      </table>

    </div>
  </div>
)}
{showSubscriptionModal && (
  <div
    className="ct-modal-backdrop"
    onClick={() => setShowSubscriptionModal(false)}
  >
    <div
      className="ct-modal"
      onClick={(e) => e.stopPropagation()}
      style={{ width: "450px", textAlign: "center" }}
    >
      <h3>Subscription Status</h3>

      <p style={{ marginTop: "15px", fontSize: "16px", lineHeight: "1.6" }}>
        <b>{daysLeft}</b> days are left before your subscription expires.
        <br /><br />
        To continue using the services without interruption, please renew your subscription.
        <br /><br />
        For renewal assistance, kindly contact the MyComplianceView team.
      </p>

      <button
        className="view-btn"
        style={{ marginTop: "15px" }}
        onClick={() => setShowSubscriptionModal(false)}
      >
        Close
      </button>

    </div>
  </div>
)}

{showDefenseModal && (
  <div className="ct-modal-backdrop" onClick={() => setShowDefenseModal(false)}>
    <div
      className="ct-modal"
      style={{ width: "500px", height: "350px" }}
      onClick={(e) => e.stopPropagation()}
    >
      <h3 style={{ textAlign: "center" }}>Compliance Defense Breakdown</h3>

      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={defenseData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis
  domain={[0, (dataMax) => dataMax + 10]}
/>
          <Tooltip />
          <Bar
  dataKey="value"
  barSize={40}
  radius={[6, 6, 0, 0]}
  cursor="pointer"
  onClick={(data) => handleDefenseClick(data)} // <-- Add this line
>
  <LabelList dataKey="value" position="top" />
  {defenseData.map((entry, index) => (
    <Cell
      key={`cell-${index}`}
      fill={entry.name === "Compliant" ? "#6a20ea" : "#cc0e0e"}
    />
  ))}
</Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
)}


      {/* Modal to show events for selected date */}
      {showModal && selectedDate && (
        <div className="ct-modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="ct-modal" onClick={e => e.stopPropagation()} style={{ maxHeight: "400px", overflowY: "auto" }}>
            <h3>Events for {selectedDate}</h3>
            {eventsForSelectedDate.length > 0 ? (
              <ul className="task-list">
                {eventsForSelectedDate.map((e) => (
                  <li key={e.id} className="task-item">
                    {e.title} ({e.extendedProps.type || "No Type"})
                  </li>
                ))}
              </ul>
            ) : (
              <p>No events on this date</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
